import React, { useState } from "react";
import {
  Grid,
  makeStyles,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import GeneralModal from "./GeneralModal";
import { TextFieldInput } from "../components-atoms";
import { ButtonTextPrimary, ButtonPrimary } from "../components-atoms";
import { inviteNewProTeam } from "../redux/actions/subscriptions";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export interface InviteProTeamData {
  FirstName: string;
  LastName: string;
  Email: string;
  IsCollectEnabled: boolean;
}

export const InviteProTeamModal = ({
  visible,
  onClose,
  onSuccess
}: IProps) => {
  const classes = useStyles();
  const [formData, setFormData] = useState<InviteProTeamData>({
    FirstName: "",
    LastName: "",
    Email: "",
    IsCollectEnabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof InviteProTeamData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const onFormSubmit = () => {
    if (!isFormValid()) {
      return;
    }

    setLoading(true);
    setError(null);

    inviteNewProTeam(
      formData,
      (response) => {
        setLoading(false);
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      },
      (err) => {
        setLoading(false);
        
        // Handle different error structures
        let errorMessage = 'Failed to send invitation. Please try again.';
        
        if (typeof err === 'string') {
          errorMessage = err;
        } else if (err?.response?.data?.Error) {
          errorMessage = err.response.data.Error;
        } else if (err?.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      }
    );
  };

  const handleClose = () => {
    // Reset form data
    setFormData({
      FirstName: "",
      LastName: "",
      Email: "",
      IsCollectEnabled: true,
    });
    setError(null);
    setLoading(false);
    onClose();
  };

  const isFormValid = () => {
    return formData.FirstName.trim() && 
           formData.LastName.trim() && 
           formData.Email.trim() &&
           /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email);
  };

  return (
    <GeneralModal
      title="Invite New Pro User"
      visible={visible}
      onClose={handleClose}
    >
      <form>
        <GeneralModal.ContentArea>
          <fieldset className={classes.fieldset}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextFieldInput
                  label="First Name"
                  value={formData.FirstName}
                  onChange={handleInputChange('FirstName')}
                  placeholder="Enter first name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={6}>
                <TextFieldInput
                  label="Last Name"
                  value={formData.LastName}
                  onChange={handleInputChange('LastName')}
                  placeholder="Enter last name"
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextFieldInput
                  label="Email Address"
                  type="email"
                  value={formData.Email}
                  onChange={handleInputChange('Email')}
                  placeholder="Enter email address"
                  required
                  disabled={loading}
                  error={!!error}
                  helperText={error}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.IsCollectEnabled}
                      onChange={handleInputChange('IsCollectEnabled')}
                      color="primary"
                      disabled={loading}
                    />
                  }
                  label="TKD Enabled"
                />
              </Grid>
            </Grid>
          </fieldset>
        </GeneralModal.ContentArea>
        
        <GeneralModal.ButtonArea>
          <ButtonTextPrimary onClick={handleClose} disabled={loading}>
            Cancel
          </ButtonTextPrimary>
          <ButtonPrimary
            onClick={onFormSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "Sending..." : "Send Invitation"}
          </ButtonPrimary>
        </GeneralModal.ButtonArea>
      </form>
    </GeneralModal>
  );
};

const useStyles = makeStyles({
  fieldset: {
    border: "none",
    padding: 0,
    margin: 0,
  },
});

export default InviteProTeamModal;
